// The maintenance gate, out of index.js (backfill PR 27): shows the maintenance screen while
// the backend reports maintenance mode or can't be reached, checks the status again every
// 60 seconds and after each change, and switches at once on the api's `maintenance-on` event
// (a 503 answer, maintenanceBus.js); otherwise it renders the app.
import { useEffect, useState } from 'react';
import api from '../../api';
import { maintenanceEmitter } from '../../maintenanceBus';
import MaintenanceScreen from '../../screens/MaintenanceScreen';

export default function MaintenanceGate({ children }) {
  const [maintenance, setMaintenance] = useState(null); // track whether the app is in maintenance mode (null = loading, true = in maintenance)

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const { data } = await api.get('/api/status'); // check current status
        setMaintenance(!!data.maintenance); // convert to boolean and store
      } catch (err) {
        console.error('Failed to fetch maintenance status:', err);
        setMaintenance(true); // fallback: assume maintenance mode
      }
    }; // fetch the maintenance mode status from the server

    fetchStatus(); // check maintenance mode status once on initial load

    const interval = setInterval(fetchStatus, 60000); // automatically check maintenance mode status every 60 seconds

    const handler = () => setMaintenance(true);
    maintenanceEmitter.on('maintenance-on', handler); // listen for the maintenance-on event api.js emits when an answer is a 503 (the backend's maintenanceGate) and flip the UI immediately

    return () => {
      clearInterval(interval);
      maintenanceEmitter.off('maintenance-on', handler);
    }; // clean up on unmount: stop interval + remove listener
  }, [maintenance]); // the status is a dependency on purpose (user decision, Oct 2026): each change checks again at once, which confirms a 503's switch and brings the app back when the status says the site is up, at the price of a second request per change (two at page load); a slow poll that lands after a faster check corrects itself at the next tick

  if (maintenance === null) {
    // if we're still checking the status:
    return (
      <div style={{ textAlign: 'center', padding: '20vh' }}>
        Checking site status…
      </div>
    );
  }

  if (maintenance === true) {
    // if site is in maintenance mode, show the MaintenanceScreen:
    return <MaintenanceScreen />;
  }

  // otherwise, render the full application normally:
  return children;
}
